import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";

import { MerchantType } from "@/Apollo/schema";
import { RETURN_POLICY_ROUTES } from "@/common/static/footer.static";
import { useContentLocale } from "@/i18n/hooks";
import { Body } from "@whoppah/ui/typography/Body";

type ProductDetailsReturnPolicyNoteProps = {
  merchantType?: MerchantType | null;
};

/**
 * One line on the return rights that apply to this listing (WHO-5927).
 * Business sellers: the legal 14-day right of withdrawal. Private sellers:
 * no right of withdrawal, but a refund when the item significantly deviates
 * from the listing. Unknown type: nothing, rather than guessing.
 */
export const ProductDetailsReturnPolicyNote = ({
  merchantType,
}: ProductDetailsReturnPolicyNoteProps) => {
  const { t } = useTranslation();
  const locale = useContentLocale();

  const textKey =
    merchantType === MerchantType.Business
      ? "pdp:pdp-return-policy-business"
      : merchantType === MerchantType.Individual
      ? "pdp:pdp-return-policy-private"
      : null;

  if (!textKey) {
    return null;
  }

  const href = RETURN_POLICY_ROUTES[locale] ?? RETURN_POLICY_ROUTES.en;

  return (
    <Body
      as="p"
      className="text-sm text-grey-700"
      data-testid="pdp.return-policy-note"
    >
      {t(textKey)}{" "}
      <Link
        href={href}
        prefetch={false}
        sourceLocale="en"
        className="underline hover:no-underline"
      >
        {t("pdp:pdp-return-policy-link")}
      </Link>
    </Body>
  );
};
